import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Users, Package, FileText, UserCog, ScrollText, Settings as SettingsIcon,
  Menu, LogOut,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/musteriler", label: "Müşteriler", icon: Users },
  { to: "/urunler", label: "Ürünler", icon: Package },
  { to: "/teklifler", label: "Teklifler", icon: FileText },
  { to: "/personel", label: "Personel", icon: UserCog, perm: "view_staff" },
  { to: "/loglar", label: "İşlem Geçmişi", icon: ScrollText, perm: "view_logs" },
  { to: "/ayarlar", label: "Ayarlar", icon: SettingsIcon, perm: "view_settings" },
];

function useNavItems() {
  const { isAdmin, can } = useAuth();
  return NAV.filter((n) => (!n.adminOnly || isAdmin) && (!n.perm || can(n.perm)));
}

function TopLink({ n, onClick }) {
  return (
    <NavLink
      to={n.to}
      end={n.end}
      onClick={onClick}
      data-testid={`nav-${n.to === "/" ? "panel" : n.to.slice(1)}`}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
          isActive ? "bg-rose-50 text-rose-700 font-semibold" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
        }`
      }
    >
      <n.icon className="w-[17px] h-[17px]" />
      {n.label}
    </NavLink>
  );
}

export function Layout({ children }) {
  const { user, logout, logoUrl } = useAuth();
  const items = useNavItems();
  const [open, setOpen] = useState(false);
  const initial = (user?.name || "?").charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6 min-w-0">
            {/* Mobile menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden rounded-xl" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 bg-white">
                <div className="h-16 flex items-center px-6 border-b border-slate-100"><Logo size="sm" logoUrl={logoUrl} /></div>
                <nav className="flex flex-col gap-1.5 p-4">
                  {items.map((n) => <TopLink key={n.to} n={n} onClick={() => setOpen(false)} />)}
                </nav>
                <div className="border-t border-slate-100 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm">{initial}</div>
                    <div><p className="text-sm font-semibold text-slate-900">{user?.name}</p><p className="text-xs text-slate-400">{ROLE_LABEL[user?.role] || "Personel"}</p></div>
                  </div>
                  <Button variant="outline" size="sm" className="w-full rounded-xl" onClick={logout} data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" />Çıkış Yap</Button>
                </div>
              </SheetContent>
            </Sheet>

            <Logo size="sm" logoUrl={logoUrl} />
            <nav className="hidden lg:flex items-center gap-1">
              {items.map((n) => <TopLink key={n.to} n={n} />)}
            </nav>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full hover:bg-slate-100 p-1 pr-2 transition-colors" data-testid="user-menu">
                <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm">{initial}</div>
                <span className="hidden sm:block text-sm font-medium text-slate-700">{user?.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{ROLE_LABEL[user?.role] || "Personel"}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-menu-item" className="text-rose-600"><LogOut className="w-4 h-4 mr-2" />Çıkış Yap</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8 animate-fade-up">{children}</main>
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
